import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Heart,
    Shield,
    ChevronRight,
    Home
} from 'lucide-react';
import { PrimaryButton, SecondaryButton, TertiaryButton } from '../components/brand/BrandButton';
import { BrandCard, SelectionCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { ModuleHeader, SafetyFooter } from '../components/brand/QuickExit';
import { getSafetyQuestions, getSafetyGuidance } from '../services/relationshipToolsService';
import { AdminEditButton } from '../components/admin/AdminEditButton';

/**
 * Relationship Safety Check
 * 
 * A gentle check-in to help users notice what feels safe, supportive, or overwhelming.
 * NOT an assessment or diagnostic tool.
 * - NEVER stores answers or sensitive data
 * - Uses soft, validating, non-judgmental language
 * - Can be exited instantly at any time
 * Now fetches questions and guidance from the database.
 */

const RelationshipSafetyCheck = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState('intro'); // intro, emotional, communication, nervous, need, guidance, closing
    const [emotionalSafety, setEmotionalSafety] = useState(null);
    const [communicationSafety, setCommunicationSafety] = useState(null);
    const [nervousSystem, setNervousSystem] = useState(null);
    const [selectedNeed, setSelectedNeed] = useState(null);
    
    // Data from database
    const [safetyQuestions, setSafetyQuestions] = useState([]);
    const [guidanceMap, setGuidanceMap] = useState({});
    const [loading, setLoading] = useState(true);

    // Clear any data on unmount (safety measure)
    useEffect(() => {
        return () => {
            setEmotionalSafety(null);
            setCommunicationSafety(null);
            setNervousSystem(null);
            setSelectedNeed(null);
        };
    }, []);

    // Fetch data from database
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [questionsData, guidanceData] = await Promise.all([
                    getSafetyQuestions(),
                    getSafetyGuidance()
                ]);
                setSafetyQuestions(questionsData);
                setGuidanceMap(guidanceData);
            } catch (error) {
                console.error('Error fetching safety check data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Helper to get question by step
    const getQuestionForStep = (stepName) => {
        const questionMap = {
            'emotional': safetyQuestions.find(q => q.step === 'emotional'),
            'communication': safetyQuestions.find(q => q.step === 'communication'),
            'nervous': safetyQuestions.find(q => q.step === 'nervous'),
            'need': safetyQuestions.find(q => q.step === 'need')
        };
        return questionMap[stepName];
    };

    // Fallback options if database is unavailable
    const frequencyOptions = [
        { id: 'often', label: 'Often' },
        { id: 'sometimes', label: 'Sometimes' },
        { id: 'rarely', label: 'Rarely' },
        { id: 'unsure', label: 'I\'m not sure' }
    ];

    const nervousSystemOptions = [
        { id: 'calm', label: 'Mostly calm' },
        { id: 'mixed', label: 'Mixed' },
        { id: 'tense', label: 'Mostly tense' },
        { id: 'unsure', label: 'I\'m not sure' }
    ];

    const needOptions = [
        { id: 'reassurance', label: 'Reassurance' },
        { id: 'space', label: 'Space' },
        { id: 'clarity', label: 'Clarity' },
        { id: 'gentleness', label: 'Gentleness' },
        { id: 'understanding', label: 'Understanding' },
        { id: 'unsure', label: 'I\'m not sure' }
    ];

    const getGuidance = () => {
        // Try database guidance first
        if (guidanceMap && guidanceMap[selectedNeed]) {
            return {
                text: guidanceMap[selectedNeed].text,
                suggestion: guidanceMap[selectedNeed].suggestion
            };
        }
        
        // Fallback to static data
        const fallbackGuidance = {
            reassurance: {
                text: 'Reassurance can help settle an anxious nervous system.',
                suggestion: 'Offer yourself a soft reminder: "I can need comfort. My feelings are real."'
            },
            space: {
                text: 'Space can help when things feel overwhelming.',
                suggestion: 'Take a moment with intention: "I\'m creating space to settle, not to disconnect."'
            },
            clarity: {
                text: 'Clarity can help when things feel confusing.',
                suggestion: 'Name one thing that feels unclear. You don\'t have to solve it — just notice it.'
            },
            gentleness: {
                text: 'Gentleness can help when things feel harsh or tense.',
                suggestion: 'Soften your inner voice. Speak to yourself the way you\'d speak to someone you love.'
            },
            understanding: {
                text: 'Understanding can help when you feel unseen.',
                suggestion: 'Acknowledge your own experience: "What I\'m feeling makes sense given what\'s happening."'
            },
            unsure: {
                text: 'It\'s okay not to know what you need.',
                suggestion: 'A small grounding moment can help. Needs often become clearer when the body settles.'
            }
        };
        return fallbackGuidance[selectedNeed] || fallbackGuidance['unsure'];
    };

    const handleBackNavigation = () => {
        if (step === 'intro') {
            navigate('/relationship-hub');
        } else if (step === 'emotional') {
            setStep('intro');
        } else if (step === 'communication') {
            setStep('emotional');
        } else if (step === 'nervous') {
            setStep('communication');
        } else if (step === 'need') {
            setStep('nervous');
        } else if (step === 'guidance') {
            setStep('need');
        }
    };

    // Calculate progress
    const getProgress = () => {
        const stepOrder = ['intro', 'emotional', 'communication', 'nervous', 'need', 'guidance', 'closing'];
        return stepOrder.indexOf(step);
    };

    return (
        <div className="min-h-screen bg-transparent">
            {/* Admin Edit Button */}
            <AdminEditButton toolType="safety_question" />
            
            {/* Orenda Module Header */}
            <ModuleHeader
                title="Safety Check"
                onBack={handleBackNavigation}
                backDestination="/relationship-hub"
                exitDestination="/home?tab=relationships"
                showBack={step !== 'closing' && step !== 'intro'}
            />

            <main className="max-w-xl mx-auto px-6 py-8">
                {/* INTRO */}
                {step === 'intro' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-10">
                            <IconContainer variant="gold" size="xl" useGradient={true} className="mx-auto mb-6">
                                <Shield className="w-8 h-8" />
                            </IconContainer>
                            
                            <BodyText className="text-[var(--orenda-text-secondary)] max-w-md mx-auto leading-relaxed mb-3">
                                This gentle check-in helps you notice what feels safe, supportive, or overwhelming in your relationship.
                            </BodyText>
                            
                            <SmallText className="text-[var(--orenda-text-muted)] max-w-sm mx-auto leading-relaxed">
                                There are no right or wrong answers. This is just for you.
                            </SmallText>
                        </div>

                        <div className="space-y-3">
                            <PrimaryButton
                                onClick={() => setStep('emotional')}
                                className="w-full"
                                size="lg"
                                data-testid="start-btn"
                            >
                                Begin
                                <ChevronRight className="w-4 h-4 ml-2" />
                            </PrimaryButton>
                        </div>
                        
                        <div className="mt-10 text-center">
                            <SmallText className="text-[var(--orenda-text-muted)] italic">
                                You can pause anytime.
                            </SmallText>
                        </div>
                    </div>
                )}

                {/* STEP 1: EMOTIONAL SAFETY */}
                {step === 'emotional' && (
                    <div className="animate-fade-in">
                        {/* Progress dots */}
                        <div className="flex justify-center gap-2 mb-8">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className={`w-2 h-2 rounded-full ${i === 1 ? 'bg-[#1D6B6B]' : 'bg-[var(--orenda-border)]'}`} />
                            ))}
                        </div>
                        
                        <div className="glass-card rounded-2xl p-6 mb-8">
                            <BodyText className="text-[var(--orenda-text-primary)] text-lg text-center leading-relaxed">
                                Do you feel emotionally safe to express your feelings?
                            </BodyText>
                        </div>

                        <div className="space-y-3 mb-6">
                            {frequencyOptions.map((option, index) => {
                                const cardVariants = ['glass-card-sage', 'glass-card-rose', 'glass-card-gold', 'glass-card'];
                                return (
                                    <button
                                        key={option.id}
                                        onClick={() => {
                                            setEmotionalSafety(option.id);
                                            setStep('communication');
                                        }}
                                        className={`w-full py-4 text-center rounded-2xl ${cardVariants[index % cardVariants.length]} card-hover transition-all`}
                                        data-testid={`emotional-${option.id}`}
                                    >
                                        <BodyText className="font-medium">{option.label}</BodyText>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="text-center mb-6">
                            <SmallText className="text-[var(--orenda-text-muted)] italic">
                                Emotional safety can shift depending on stress, context, and nervous system state.
                            </SmallText>
                        </div>

                        <SafetyFooter
                            destination="/relationship-hub"
                            exitLabel="Come back later"
                            reassurance="Take your time."
                        />
                    </div>
                )}

                {/* STEP 2: COMMUNICATION SAFETY */}
                {step === 'communication' && (
                    <div className="animate-fade-in">
                        <div className="flex justify-center gap-2 mb-8">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className={`w-2 h-2 rounded-full ${i <= 2 ? 'bg-[#1D6B6B]' : 'bg-[var(--orenda-border)]'}`} />
                            ))}
                        </div>
                        
                        <div className="glass-card rounded-2xl p-6 mb-8">
                            <BodyText className="text-[var(--orenda-text-primary)] text-lg text-center leading-relaxed">
                                Do conversations feel respectful and steady most of the time?
                            </BodyText>
                        </div>

                        <div className="space-y-3 mb-6">
                            {frequencyOptions.map((option, index) => {
                                const cardVariants = ['glass-card-sage', 'glass-card-rose', 'glass-card-gold', 'glass-card'];
                                return (
                                    <button
                                        key={option.id}
                                        onClick={() => {
                                            setCommunicationSafety(option.id);
                                            setStep('nervous');
                                        }}
                                        className={`w-full py-4 text-center rounded-2xl ${cardVariants[index % cardVariants.length]} card-hover transition-all`}
                                        data-testid={`communication-${option.id}`}
                                    >
                                        <BodyText className="font-medium">{option.label}</BodyText>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="text-center mb-6">
                            <SmallText className="text-[var(--orenda-text-muted)] italic">
                                Communication patterns are shaped by stress, overwhelm, and past experiences.
                            </SmallText>
                        </div>

                        <SafetyFooter
                            destination="/relationship-hub"
                            exitLabel="Come back later"
                            reassurance="There's no rush."
                        />
                    </div>
                )}

                {/* STEP 3: NERVOUS SYSTEM SAFETY */}
                {step === 'nervous' && (
                    <div className="animate-fade-in">
                        <div className="flex justify-center gap-2 mb-8">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className={`w-2 h-2 rounded-full ${i <= 3 ? 'bg-[#1D6B6B]' : 'bg-[var(--orenda-border)]'}`} />
                            ))}
                        </div>
                        
                        <div className="glass-card rounded-2xl p-6 mb-8">
                            <BodyText className="text-[var(--orenda-text-primary)] text-lg text-center leading-relaxed">
                                Does your body feel calm or tense around your partner?
                            </BodyText>
                        </div>

                        <div className="space-y-3 mb-6">
                            {nervousSystemOptions.map((option, index) => {
                                const cardVariants = ['glass-card-sage', 'glass-card-rose', 'glass-card-gold', 'glass-card'];
                                return (
                                    <button
                                        key={option.id}
                                        onClick={() => {
                                            setNervousSystem(option.id);
                                            setStep('need');
                                        }}
                                        className={`w-full py-4 text-center rounded-2xl ${cardVariants[index % cardVariants.length]} card-hover transition-all`}
                                        data-testid={`nervous-${option.id}`}
                                    >
                                        <BodyText className="font-medium">{option.label}</BodyText>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="text-center mb-6">
                            <SmallText className="text-[var(--orenda-text-muted)] italic">
                                Your body often speaks before your mind does.
                            </SmallText>
                        </div>

                        <SafetyFooter
                            destination="/relationship-hub"
                            exitLabel="Come back later"
                            reassurance="You get to choose what feels right."
                        />
                    </div>
                )}

                {/* STEP 4: WHAT YOU NEED */}
                {step === 'need' && (
                    <div className="animate-fade-in">
                        <div className="flex justify-center gap-2 mb-8">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className={`w-2 h-2 rounded-full bg-[#1D6B6B]`} />
                            ))}
                        </div>
                        
                        <div className="glass-card rounded-2xl p-6 mb-8">
                            <BodyText className="text-[var(--orenda-text-primary)] text-lg text-center leading-relaxed">
                                What would help you feel a little safer or more supported right now?
                            </BodyText>
                        </div>

                        <div className="space-y-3 mb-8">
                            {needOptions.map((option, index) => {
                                const cardVariants = ['glass-card-sage', 'glass-card-rose', 'glass-card-lavender', 'glass-card-gold', 'glass-card'];
                                return (
                                    <button
                                        key={option.id}
                                        onClick={() => {
                                            setSelectedNeed(option.id);
                                            setStep('guidance');
                                        }}
                                        className={`w-full py-4 text-center rounded-2xl ${cardVariants[index % cardVariants.length]} card-hover transition-all`}
                                        data-testid={`need-${option.id}`}
                                    >
                                        <BodyText className="font-medium">{option.label}</BodyText>
                                    </button>
                                );
                            })}
                        </div>

                        <SafetyFooter
                            destination="/relationship-hub"
                            exitLabel="Come back later"
                            reassurance="All needs are valid."
                        />
                    </div>
                )}

                {/* STEP 5: GENTLE GUIDANCE */}
                {step === 'guidance' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-6">
                            <IconContainer variant="sage" size="lg" useGradient={true} className="mx-auto mb-4">
                                <Heart className="w-5 h-5" />
                            </IconContainer>
                        </div>

                        <div className="glass-card-sage rounded-2xl p-6 mb-6">
                            <BodyText className="text-[var(--orenda-text-primary)] text-center leading-relaxed mb-4">
                                {getGuidance().text}
                            </BodyText>
                            <div className="p-4 rounded-xl bg-[#7D8E95]/10 border border-[#7D8E95]/20">
                                <SmallText className="text-[var(--orenda-text-secondary)] text-center leading-relaxed">
                                    {getGuidance().suggestion}
                                </SmallText>
                            </div>
                        </div>
                        
                        <div className="text-center mb-6">
                            <SmallText className="text-[var(--orenda-text-muted)] italic">
                                This is an invitation, not an instruction.
                            </SmallText>
                        </div>

                        <PrimaryButton
                            onClick={() => setStep('closing')}
                            className="w-full"
                            size="lg"
                        >
                            Continue
                            <ChevronRight className="w-4 h-4 ml-2" />
                        </PrimaryButton>

                        <SafetyFooter
                            destination="/relationship-hub"
                            exitLabel="Come back later"
                            reassurance=""
                        />
                    </div>
                )}

                {/* CLOSING */}
                {step === 'closing' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-6">
                            <IconContainer variant="sage" size="xl" useGradient={true} className="mx-auto mb-4">
                                <Shield className="w-6 h-6" />
                            </IconContainer>
                        </div>

                        <div className="glass-card-sage rounded-2xl p-6 mb-6">
                            <BodyText className="text-[var(--orenda-text-primary)] text-center leading-relaxed text-lg">
                                Thank you for checking in with yourself.
                            </BodyText>
                        </div>
                        
                        <div className="text-center mb-8">
                            <SmallText className="text-[var(--orenda-text-muted)] italic">
                                Your feelings and needs matter.
                            </SmallText>
                        </div>

                        <div className="space-y-3">
                            <PrimaryButton
                                onClick={() => navigate('/relationship-hub')}
                                className="w-full"
                                size="lg"
                                data-testid="return-to-hub-btn"
                            >
                                <Home className="w-4 h-4 mr-2" />
                                Return to Hub
                            </PrimaryButton>
                            <SecondaryButton
                                onClick={() => {
                                    setStep('intro');
                                    setEmotionalSafety(null);
                                    setCommunicationSafety(null);
                                    setNervousSystem(null);
                                    setSelectedNeed(null);
                                }}
                                className="w-full"
                                size="lg"
                            >
                                Check in again
                            </SecondaryButton>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default RelationshipSafetyCheck;
